<template>
  <el-dialog
    :visible.sync="openModal"
    title="Project Settings"
    width="800px"
    @close="reset">
    <form
      class="project-form"
      id="form"
      @submit.prevent="submit">
      <video-settings
        v-if="currentVideoId"
        ref="videoInfo"
        :details="videoDetails" />
      <div v-if="currentVideoId" class="divider" />
      <price-info
        :currency-icon="getCurrencyIcon(currencyId)"
        :videos="videos" />
    </form>
    <template slot="footer">
      <currency-select
        v-if="!currentVideoId"
        :value="currencyId"
        @change="v => currencyId = v" />
      <el-button @click="openModal = false">Cancel</el-button>
      <el-button
        form="form"
        native-type="submit"
        type="primary"
        :loading="loading">
        Save
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { updateProjectSettings } from "@/api/projects";
import { updateVideo } from "@/api/files";
import { DEFAULT_CURRENCY_ID, DEFAULT_PRICE_FIELDS } from '@/constants';
import CurrencySelect from '@/components/CurrencySelect';
import PriceInfo from '@/components/PriceInfo';
import VideoSettings from './VideoSettings';

export default {
  components: {
    CurrencySelect,
    PriceInfo,
    VideoSettings,
  },
  name: "ProjectSettings",
  props: {
    project: {
      type: Object,
      required: true,
    },
    videosInfo: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currencyId: null,
      currentVideoId: null,
      loading: false,
      openModal: false,
      videos: [],
    }
  },
  computed: {
    ...mapGetters({
      getCurrencyIcon: 'getCurrencyIcon',
    }),
    projectId() {
      return this.$route.params.projectId;
    },
    videoDetails() {
      if (!this.currentVideoId) {
        return {};
      }
      return this.videosInfo.find(el => el.id === this.currentVideoId);
    },
  },
  watch: {
    openModal(value) {
      if (value) {
        this.getSettings();
      }
    },
  },
  methods: {
    ...mapActions({
      error: 'error',
    }),
    getSettings() {
      const currentVideos = this.videosInfo.map(el => ({
        ...DEFAULT_PRICE_FIELDS,
        ...el.priceSettings,
      }));

      this.currencyId = this.project?.currency_id || DEFAULT_CURRENCY_ID;
      this.videos = this.currentVideoId
        ? currentVideos.filter(el => el.video_id === this.currentVideoId)
        : currentVideos;
    },
    open() {
      this.openModal = true;
    },
    reset() {
      this.currentVideoId = null;
      this.videos = [];
    },
    setInitial(id) {
      if (id) {
        this.currentVideoId = id;
      }
    },
    updateProjectInfo() {
      const videos_settings = this.videos.map(({ broadcast, length, price, video_id }) => ({
        broadcast,
        length: Number(length),
        price: Number(price),
        video_id,
      }));
      return updateProjectSettings(this.projectId, {
        currency_id: this.currencyId,
        videos_settings,
      });
    },
    updateVideoInfo() {
      const { date, name, place } = this.$refs.videoInfo.getData();
      const formData = new FormData();
      formData.append('event_date', date ? moment(date).format('YYYY-MM-DD') : '');
      formData.append('event_name', name || '');
      formData.append('event_place', place || '');
      return updateVideo(formData, this.currentVideoId);
    },
    async submit() {
      this.loading = true;
      try {
        const promiseArr = [this.updateProjectInfo()];
        if (this.currentVideoId) {
          promiseArr.push(this.updateVideoInfo());
        }
        const responses = await Promise.all(promiseArr);
        const errors = responses.reduce((res, { data: { error, msg } }) => {
          if (error) {
            res.push(msg);
          }
          return res;
        }, []);

        if (errors.length) {
          errors.forEach(el => this.error(el));
        } else {
          this.$emit('update');
          this.openModal = false;
          this.reset();
        }
      } finally {
        this.loading = false;
      }
    },
  },
}
</script>

<style scoped lang="scss">
.project-form {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.divider {
  height: 1px;
  background: var(--border-color);
}

::v-deep {
  .el-dialog__footer {
    display: flex;
    justify-content: flex-end;

    .el-button {
      padding: 8px 16px;
    }
  }

  .el-input__inner {
    color: var(--primary);
  }
}
</style>
